package org.mkui.component.menu

import com.macrofocus.common.command.UICommand
import kotlinx.browser.document
import kotlinx.browser.window
import org.mkui.component.CPComponent
import org.w3c.dom.HTMLDivElement
import org.w3c.dom.events.Event
import org.w3c.dom.events.MouseEvent
import react.FC
import react.Props

/**
 * A popup menu of commands, a null command adding a separator; the items are enabled as their command when shown. The
 * React components do not expose their element, so the menu opens where the mouse was last pressed (menus are opened
 * from mouse presses), in a box of the page closed by a press outside it or by Escape.
 */
actual class CPPopupMenu : CPComponent {
    private val commands: MutableList<UICommand?> = ArrayList()

    override val nativeComponent: FC<*> = FC<Props> {}

    actual fun add(command: UICommand?) {
        commands.add(command)
    }

    actual fun show(component: CPComponent, x: Int, y: Int) {
        close()
        val menu = document.createElement("div") as HTMLDivElement
        menu.style.cssText = "position: fixed; z-index: 10000; min-width: 120px; padding: 4px 0; background: white; " +
            "border: 1px solid #bebebe; border-radius: 4px; box-shadow: 0 2px 6px rgba(0, 0, 0, 0.25); " +
            "font: 12px sans-serif; user-select: none;"
        for (command in commands) {
            if (command == null) {
                val separator = document.createElement("div") as HTMLDivElement
                separator.style.cssText = "height: 1px; margin: 2px 0; background: #dcdcdc;"
                menu.appendChild(separator)
            } else {
                val enabled = command.enabledProperty()?.value != false
                val item = document.createElement("div") as HTMLDivElement
                item.textContent = command.titleProperty()?.value ?: ""
                item.style.cssText = "padding: 3px 12px; white-space: nowrap; cursor: default;" + if (enabled) "" else " color: #a0a0a0;"
                if (enabled) {
                    item.addEventListener("mouseenter", { item.style.background = "#e8e8e8" })
                    item.addEventListener("mouseleave", { item.style.background = "" })
                    item.addEventListener("click", {
                        close()
                        command.execute(component)
                    })
                }
                menu.appendChild(item)
            }
        }
        menu.addEventListener("mousedown", { it.stopPropagation() })
        document.body?.appendChild(menu)
        // Within the window
        val left = minOf(lastX, window.innerWidth - menu.offsetWidth).coerceAtLeast(0)
        val top = minOf(lastY, window.innerHeight - menu.offsetHeight).coerceAtLeast(0)
        menu.style.left = "${left}px"
        menu.style.top = "${top}px"
        shown = menu
        openingEvent = pressEvent
    }

    companion object {
        private var shown: HTMLDivElement? = null
        private var lastX = 0
        private var lastY = 0

        /** The press being dispatched, and the one that opened the menu, which must not close it. */
        private var pressEvent: Event? = null
        private var openingEvent: Event? = null

        private fun close() {
            shown?.remove()
            shown = null
        }

        init {
            val track: (Event) -> Unit = { event ->
                pressEvent = event
                (event as? MouseEvent)?.let {
                    lastX = it.clientX
                    lastY = it.clientY
                }
            }
            window.addEventListener("mousedown", track, true)
            window.addEventListener("contextmenu", track, true)
            // A press outside the menu closes it
            window.addEventListener("mousedown", { if (it !== openingEvent) close() })
            window.addEventListener("keydown", { if (it.asDynamic().key == "Escape") close() })
        }
    }
}
